import type { DataBinding } from "../layout/types.js";
import {
  buildBarLoaderDiskStylesForRender,
  getBarLoaderFootprint,
  isBarLoaderCompactVariableWidth,
  type BarLoaderProps,
} from "../layout/bar-loader-config.js";
import { dependencyFromDataBindingPath } from "./renderCache.js";
import type {
  RenderBarLoaderNode,
  RenderBarLoaderProps,
  RuntimeContext,
} from "./types.js";
import { resolvePath } from "./method.js";

/**
 * Resolves the total weight shown on a bar-loader node after behavior patches.
 *
 * @param props - Props updated by runtime patches (`totalWeight`, `barWeight`, etc.).
 * @param dataBinding - Optional numeric binding on the layout node.
 * @param ctx - Current injected data.
 * @returns Finite total weight in kg or lbs according to `props.unit`.
 */
export function resolveCompiledBarLoaderTotalWeight(
  props: RenderBarLoaderProps,
  dataBinding: DataBinding | undefined,
  ctx: RuntimeContext,
): number {
  const fromProps = Number(props.totalWeight);
  if (Number.isFinite(fromProps)) {
    return fromProps;
  }

  if (dataBinding?.path) {
    const fromBinding = Number(
      resolvePath(ctx, dependencyFromDataBindingPath(dataBinding.path)),
    );
    if (Number.isFinite(fromBinding)) {
      return fromBinding;
    }
  }

  return 0;
}

/**
 * Applies variable compact bar-loader footprint dimensions on the compiled render node.
 */
export function applyCompiledBarLoaderVariableFootprint(
  renderNode: RenderBarLoaderNode,
  totalWeight: number,
): RenderBarLoaderNode {
  const props = renderNode.props as BarLoaderProps;
  if (!isBarLoaderCompactVariableWidth(props)) {
    return renderNode;
  }

  const diskStyles = buildBarLoaderDiskStylesForRender(
    props,
    renderNode.barLoaderTargetPatches,
  );
  const footprint = getBarLoaderFootprint(
    { ...props, totalWeight },
    diskStyles,
  );

  return {
    ...renderNode,
    width: footprint.width,
    height: Math.max(renderNode.height, footprint.height),
  };
}
